import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, useHistory } from "react-router-dom";
import {
  FaCalendarAlt,
  FaComments,
  FaHome,
  FaReply,
  FaSyncAlt,
  FaUser,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import MdlSelectSession from "../dialog/MdlSelectSession";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/Comment.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const PARENTS_API = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR_FOR_PARENTS}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const PAGE_SIZE = 40;

// Created By Cursor
function formatWhen(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Created By Cursor
function initials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase();
}

// Created By Cursor
export default function Comment() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [comments, setComments] = useState([]);
  const [search, setSearch] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [replyItem, setReplyItem] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const current = SessionManager.getCurrentSession()?.[0];
      if (current?.session_id) {
        setSessionId(current.session_id);
        setSessionName(current.session_name || "");
      }
    } catch (e) {
      // ignore
    }
    setSessionReady(true);
  }, []);

  const loadComments = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        setComments([]);
        setLoading(false);
        return;
      }
      if (soft) setRefreshing(true);
      else setLoading(true);
      try {
        const response = await axios
          .get(`${API_BASE()}getAllComments.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              limit: PAGE_SIZE,
              offset: 0,
            },
          })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          setComments(Array.isArray(response.comment) ? response.comment : []);
        } else {
          setComments([]);
          const remark = String(response?.api_remark || "");
          if (!/no record/i.test(remark)) {
            myToast.notify_warning(remark || "Could not load comments.");
          }
        }
      } catch (error) {
        console.error(error);
        setComments([]);
        myToast.notify_warning("Failed to load comments.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loggedIn || !sessionReady) return;
    if (!sessionId) {
      setLoading(false);
      return;
    }
    loadComments();
  }, [loggedIn, sessionReady, sessionId, loadComments]);

  const onSessionPicked = useCallback((id, name) => {
    setSessionId(id || "");
    setSessionName(name || "");
    setSessionDialogOpen(false);
    setComments([]);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return comments;
    return comments.filter((item) => {
      const hay = [
        item.comment,
        item.comment_title,
        item.comment_for,
        item.comment_type,
        item.sender_name,
        item.sender_id,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [comments, search]);

  const openReply = (item) => {
    setReplyItem(item);
    setReplyText("");
  };

  const submitReply = async () => {
    const text = String(replyText || "").trim();
    if (!text) {
      myToast.notify_warning("Please enter a reply.");
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    const staffId = SessionManager.getStaffDetails()?.staff_id;
    if (!schoolCode || !staffId || !replyItem?.comment_id) {
      myToast.notify_warning("Missing staff or comment details.");
      return;
    }
    setSaving(true);
    try {
      const response = await axios
        .get(`${PARENTS_API()}addComments.php`, {
          params: {
            school_code: schoolCode,
            comment: text,
            sender_id: staffId,
            comment_for: replyItem.comment_for || "Parent",
            comment_type: "Reply",
            parent_comment_id: replyItem.comment_id,
          },
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success("Reply sent.");
        setReplyItem(null);
        setReplyText("");
        await loadComments({ soft: true });
      } else {
        myToast.notify_warning(response?.api_remark || "Could not send reply.");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Failed to send reply.");
    } finally {
      setSaving(false);
    }
  };

  const busy = loading || refreshing || saving;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page pc-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Parents Comments</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell pc-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaComments />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Parents Comments</h1>
              <p className="sm-hero__sub">
                {sessionName
                  ? `${sessionName} · suggestions, queries and feedback`
                  : "Pick a session to view parent comments"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search comments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search comments"
              />
            </div>
            <button
              type="button"
              className="pc-session"
              onClick={() => setSessionDialogOpen(true)}
              disabled={busy}
              title="Change session"
            >
              <FaCalendarAlt aria-hidden />
              <span>
                <small>Session</small>
                <strong>{sessionName || "Select"}</strong>
              </span>
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadComments({ soft: true })}
              disabled={busy || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="pc-body">
          {loading && !filtered.length ? (
            <div className="pc-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !sessionId ? (
            <div className="pc-empty">
              <p className="pc-empty__title">Select a session</p>
              <p className="pc-empty__hint">
                Choose a session to load parent comments.
              </p>
              <button
                type="button"
                className="pc-btn pc-btn--primary"
                onClick={() => setSessionDialogOpen(true)}
              >
                <FaCalendarAlt aria-hidden /> Select session
              </button>
            </div>
          ) : null}

          {!loading && sessionId && !filtered.length ? (
            <div className="pc-empty">
              <div className="pc-empty__mark" aria-hidden>
                <FaComments />
              </div>
              <p className="pc-empty__title">
                {search.trim() ? "No matching comments" : "No comments yet"}
              </p>
              <p className="pc-empty__hint">
                {search.trim()
                  ? "Try a different search term."
                  : "Parent suggestions and queries will appear here."}
              </p>
            </div>
          ) : null}

          {filtered.length ? (
            <div className="pc-list">
              {filtered.map((item) => {
                const replies = Array.isArray(item.reply) ? item.reply : [];
                const name = item.sender_name || item.sender_id || "Parent";
                return (
                  <article
                    key={item.comment_id}
                    className="pc-card"
                  >
                    <div className="pc-card__top">
                      <div className="pc-avatar" aria-hidden>
                        {initials(name)}
                      </div>
                      <div className="pc-card__who">
                        <strong>{name}</strong>
                        <small>
                          {item.sender_id || "—"} · {formatWhen(item.date_added)}
                        </small>
                      </div>
                      <div className="pc-card__tags">
                        {item.comment_type ? (
                          <span className="pc-pill">{item.comment_type}</span>
                        ) : null}
                        {item.comment_for ? (
                          <span className="pc-pill pc-pill--to">
                            To {item.comment_for}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {item.comment_title ? (
                      <h2 className="pc-card__title">{item.comment_title}</h2>
                    ) : null}
                    <p className="pc-card__body">{item.comment || "—"}</p>
                    {replies.length ? (
                      <div className="pc-replies">
                        <h3>Replies · {replies.length}</h3>
                        {replies.map((reply) => (
                          <div
                            key={reply.comment_id || reply.reply_id}
                            className="pc-reply"
                          >
                            {reply.staff_image_path ? (
                              <img
                                src={reply.staff_image_path}
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <span className="pc-reply__fallback" aria-hidden>
                                <FaUser />
                              </span>
                            )}
                            <div>
                              <strong>
                                {reply.sender_name || reply.sender_id || "Staff"}
                              </strong>
                              <p>{reply.comment}</p>
                              <small>{formatWhen(reply.date_added)}</small>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <footer className="pc-card__foot">
                      <button
                        type="button"
                        className="pc-btn pc-btn--primary"
                        onClick={() => openReply(item)}
                        disabled={busy}
                      >
                        <FaReply aria-hidden /> Reply
                      </button>
                    </footer>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {replyItem ? (
        <div
          className="pc-modal-backdrop"
          role="presentation"
          onClick={() => {
            if (!saving) setReplyItem(null);
          }}
        >
          <div
            className="pc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pc-reply-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pc-modal__head">
              <h2 id="pc-reply-title">Reply to comment</h2>
              <button
                type="button"
                className="pc-modal__close"
                onClick={() => !saving && setReplyItem(null)}
                disabled={saving}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="pc-modal__body">
              <p className="pc-modal__quote">{replyItem.comment}</p>
              <label className="pc-field">
                <span className="pc-field__label">Your reply</span>
                <textarea
                  className="pc-textarea"
                  rows={5}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  disabled={saving}
                  placeholder="Write a reply for the parent"
                />
              </label>
            </div>
            <footer className="pc-modal__foot">
              <button
                type="button"
                className="pc-btn pc-btn--ghost"
                onClick={() => setReplyItem(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pc-btn pc-btn--primary"
                onClick={submitReply}
                disabled={saving}
              >
                {saving ? "Sending…" : "Send reply"}
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {sessionDialogOpen ? (
        <MdlSelectSession
          action={() => setSessionDialogOpen(false)}
          action2={onSessionPicked}
        />
      ) : null}
    </div>
  );
}
